(function () {
  const initializedAttribute = 'data-custom-size-pricing-initialized';
  const cropImagePropertyName = 'properties[Crop Image]';
  const cropPreferencePropertyName = 'properties[Crop Preference]';
  const customerUploadSource = 'customer-upload';
  const maxCustomerUploadImages = 3;
  const customerUploadTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const customSizePropertyNames = [
    'properties[_custom_size]',
    'properties[_custom_size_rule]',
    'properties[_custom_unit]',
    'properties[Custom Width]',
    'properties[Custom Height]',
    'properties[Custom Unit]',
    'properties[Custom Material]',
    'properties[Custom Area (m2)]',
    cropPreferencePropertyName,
    'properties[_frontend_calculated_price]',
  ];
  const cropExportType = 'image/jpeg';
  const cropExportQuality = 0.92;
  const maxCropExportDimension = 3000;

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const roundTo = (value, places) => {
    const factor = Math.pow(10, places);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  };

  const ceilTo = (value, places) => {
    const factor = Math.pow(10, places);
    return Math.ceil((value - Number.EPSILON) * factor) / factor;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getDistance = (pointA, pointB) => Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);

  const getMidpoint = (pointA, pointB) => ({
    x: (pointA.x + pointB.x) / 2,
    y: (pointA.y + pointB.y) / 2,
  });

  const hasMaxTwoDecimals = (value) => /^\d+(\.\d{1,2})?$/.test(String(value).trim());
  const getCustomerUploadPropertyName = (index) => `properties[Image${index + 1}]`;

  const parseVariantDimension = (value) => {
    const text = String(value || '')
      .trim()
      .replace(/[’‘′]/g, "'");
    if (!text) return null;

    const normalizeUnit = (unit) => {
      const normalized = String(unit || '').trim().toLowerCase();
      if (normalized === 'ft' || normalized === 'pi' || normalized === "'") return 'ft';
      if (normalized === 'm') return 'm';
      return '';
    };
    const normalizeLabel = (label) => {
      const normalized = String(label || '').trim().toLowerCase();
      if (normalized === 'w' || normalized === 'width') return 'width';
      if (normalized === 'h' || normalized === 'height') return 'height';
      return '';
    };
    const dimensionPattern =
      /(\d+(?:\.\d{1,2})?)\s*(ft|m|pi|')?\s*(w(?:idth)?|h(?:eight)?)?\s*[x×*]\s*(\d+(?:\.\d{1,2})?)\s*(ft|m|pi|')?\s*(w(?:idth)?|h(?:eight)?)?(?=\s*(?:$|[-(/),]))/gi;

    let match;
    while ((match = dimensionPattern.exec(text))) {
      const leftUnit = normalizeUnit(match[2]);
      const rightUnit = normalizeUnit(match[5]);
      if (!leftUnit && !rightUnit) continue;
      if (leftUnit && rightUnit && leftUnit !== rightUnit) return null;

      const leftLabel = normalizeLabel(match[3]);
      const rightLabel = normalizeLabel(match[6]);
      if (leftLabel && rightLabel && leftLabel === rightLabel) return null;

      let widthRaw = match[1];
      let heightRaw = match[4];
      if (leftLabel === 'height' || rightLabel === 'width') {
        widthRaw = match[4];
        heightRaw = match[1];
      }

      const width = toNumber(widthRaw);
      const height = toNumber(heightRaw);
      if (
        width === null ||
        height === null ||
        width <= 0 ||
        height <= 0 ||
        !hasMaxTwoDecimals(widthRaw) ||
        !hasMaxTwoDecimals(heightRaw)
      ) {
        return null;
      }

      return {
        widthRaw,
        heightRaw,
        unit: leftUnit || rightUnit,
      };
    }

    return null;
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    if (url.indexOf('//') === 0) return `https:${url}`;
    return url;
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load crop image.'));
      image.src = normalizeImageUrl(src);
    });

  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Unable to export crop image.'));
            }
          },
          type,
          quality
        );
      } catch (error) {
        reject(error);
      }
    });

  const formatMoney = (cents) => {
    const settings = window.themeVariables && window.themeVariables.settings ? window.themeVariables.settings : {};
    const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';

    const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      const number = (amount / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };

    let value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  };

  class CustomSizePricingController {
    constructor(root) {
      this.root = root;
      this.formId = root.getAttribute('form-id');
      this.productForm = document.getElementById(this.formId);

      if (!this.productForm) {
        this.root.hidden = true;
        return;
      }

      this.config = this.readJson('[data-custom-size-pricing-config]');
      this.variants = this.readJson('[data-custom-size-pricing-variants]') || [];
      this.images = (this.readJson('[data-custom-size-pricing-images]') || []).filter((image) => image && image.src);
      this.messages = (this.config && this.config.messages) || {};
      this.cropSource = root.dataset.cropSource || 'product-media';
      this.customerUploadMode = this.cropSource === customerUploadSource;

      if (!this.config || !this.config.enabled) {
        this.root.hidden = true;
        return;
      }

      this.body = this.query('[data-custom-size-body]');
      this.widthInput = this.query('[data-custom-size-width]');
      this.heightInput = this.query('[data-custom-size-height]');
      this.unitSelect = this.query('[data-custom-size-unit]');
      this.message = this.query('[data-custom-size-message]');
      this.warning = this.query('[data-custom-size-warning]');
      this.result = this.query('[data-custom-size-result]');
      this.priceOutput = this.query('[data-custom-size-price]');
      this.materialOutput = this.query('[data-custom-size-material]');
      this.seamNotice = this.query('[data-custom-size-seam-notice]');
      this.seamOpenButton = this.query('[data-custom-size-seam-open]');
      this.seamThumbnail = this.query('[data-custom-size-seam-thumb]');
      this.seamText = this.query('[data-custom-size-seam-text]');
      this.seamModal = this.query('[data-custom-size-seam-modal]');
      this.seamModalImage = this.query('[data-custom-size-seam-modal-image]');
      this.cropSection = this.query('[data-custom-size-crop]');
      this.cropOpenButton = this.query('[data-custom-size-crop-open]');
      this.cropStatus = this.query('[data-custom-size-crop-status]');
      this.cropPreview = this.query('[data-custom-size-crop-preview]');
      this.cropPreviewImage = this.query('[data-custom-size-crop-preview-image]');
      this.cropRemoveButton = this.query('[data-custom-size-crop-remove]');
      this.cropModal = this.query('[data-custom-size-crop-modal]');
      this.cropCanvas = this.query('[data-custom-size-crop-canvas]');
      this.cropCanvasContext = this.cropCanvas ? this.cropCanvas.getContext('2d') : null;
      this.cropThumbs = this.query('[data-custom-size-crop-thumbs]');
      this.cropModalMessage = this.query('[data-custom-size-crop-modal-message]');
      this.cropConfirmButton = this.query('[data-custom-size-crop-confirm]');
      this.cropFileInput = this.query('[data-custom-size-crop-file]');
      this.cropModalTitle = this.query('[data-custom-size-crop-title]');
      this.uploadSection = this.query('[data-custom-size-upload]');
      this.uploadList = this.query('[data-custom-size-upload-list]');
      this.uploadInput = this.query('[data-custom-size-upload-input]');
      this.uploadButton = this.query('[data-custom-size-upload-button]');
      this.uploadHint = this.query('[data-custom-size-upload-hint]');
      this.uploadFileInputs = Array.from(this.root.querySelectorAll('[data-custom-size-upload-file]'));
      this.guideOpenButton = this.query('[data-custom-size-guide-open]');
      this.guideModal = this.query('[data-custom-size-guide-modal]');
      this.hiddenFields = {
        flag: this.query('[data-custom-size-flag]'),
        rule: this.query('[data-custom-size-rule]'),
        width: this.query('[data-custom-size-property-width]'),
        height: this.query('[data-custom-size-property-height]'),
        unit: this.query('[data-custom-size-property-unit]'),
        crop: this.query('[data-custom-size-property-crop]'),
        price: this.query('[data-custom-size-property-price]'),
      };
      this.currentVariant = this.findVariantById(this.getCurrentVariantId()) || this.findVariantById(this.root.dataset.initialVariantId);
      this.currentDimensionSignature = '';
      this.currentSeamNotice = null;
      this.seamModalOpen = false;
      this.currentCropDimensionLabel = '';
      this.cropAspectRatio = null;
      this.cropFile = null;
      this.cropPreviewUrl = '';
      this.cropModalOpen = false;
      this.activeCropImageIndex = 0;
      this.cropImageElement = null;
      this.cropImageLoadingId = 0;
      this.cropCanvasSize = { width: 0, height: 0 };
      this.cropImageRect = null;
      this.cropRect = null;
      this.cropDrag = null;
      this.cropPointers = new Map();
      this.cropPinch = null;
      this.uploadItems = [];
      this.uploadEnabled = false;
      this.fixedVariantDimension = null;
      this.lastProcessedSizeOptionValue = null;
      this.guideModalOpen = false;
      this.boundResizeCropCanvas = this.resizeCropCanvas.bind(this);
      this.boundDocumentKeyDown = this.onDocumentKeyDown.bind(this);

      if (this.unitSelect && this.config.defaultUnit) {
        this.unitSelect.value = this.config.defaultUnit;
      }

      if (this.customerUploadMode && this.cropModalTitle) {
        this.cropModalTitle.textContent = this.getMessage('cropUploadedImage', 'Crop Uploaded Image');
      }

      this.renderCropThumbnails();
      this.renderCustomerUploads();
      this.bindEvents();
      this.updateCropUi(false);
      this.update();
    }

    query(selector) {
      return this.root.querySelector(selector);
    }

    getMessage(key, fallback) {
      return this.messages && this.messages[key] ? this.messages[key] : fallback;
    }

    readJson(selector) {
      const script = this.query(selector);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Unable to parse custom size pricing JSON.', error);
        return null;
      }
    }

    bindEvents() {
      this.widthInput.addEventListener('input', this.update.bind(this));
      this.heightInput.addEventListener('input', this.update.bind(this));
      this.unitSelect.addEventListener('change', this.update.bind(this));
      this.productForm.addEventListener('variant:changed', this.onVariantChanged.bind(this));
      this.productForm.addEventListener('formdata', this.onFormData.bind(this));

      if (this.cropOpenButton) {
        this.cropOpenButton.addEventListener('click', this.openCropModal.bind(this));
      }

      if (this.seamOpenButton) {
        this.seamOpenButton.addEventListener('click', this.openSeamModal.bind(this));
      }

      if (this.cropRemoveButton) {
        this.cropRemoveButton.addEventListener('click', this.clearCropSelection.bind(this));
      }

      if (this.uploadInput) {
        this.uploadInput.addEventListener('change', this.onUploadInputChange.bind(this));
      }

      if (this.guideOpenButton) {
        this.guideOpenButton.addEventListener('click', this.openGuideModal.bind(this));
      }

      this.root.querySelectorAll('[data-custom-size-seam-close]').forEach((button) => {
        button.addEventListener('click', this.closeSeamModal.bind(this));
      });

      this.root.querySelectorAll('[data-custom-size-crop-close]').forEach((button) => {
        button.addEventListener('click', this.closeCropModal.bind(this));
      });

      this.root.querySelectorAll('[data-custom-size-guide-close]').forEach((button) => {
        button.addEventListener('click', this.closeGuideModal.bind(this));
      });

      if (this.guideModal) {
        this.guideModal.addEventListener('click', (event) => {
          if (event.target === this.guideModal) {
            this.closeGuideModal();
          }
        });
      }

      if (this.cropConfirmButton) {
        this.cropConfirmButton.addEventListener('click', this.confirmCrop.bind(this));
      }

      if (this.cropCanvas) {
        this.cropCanvas.addEventListener('pointerdown', this.onCropPointerDown.bind(this));
        this.cropCanvas.addEventListener('pointermove', this.onCropPointerMove.bind(this));
        this.cropCanvas.addEventListener('pointerup', this.onCropPointerUp.bind(this));
        this.cropCanvas.addEventListener('pointercancel', this.onCropPointerUp.bind(this));
        this.cropCanvas.addEventListener('wheel', this.onCropWheel.bind(this), { passive: false });
      }

      window.addEventListener('resize', this.boundResizeCropCanvas);
      document.addEventListener('keydown', this.boundDocumentKeyDown);

      const productVariants = this.getProductVariantsElement();
      if (productVariants) {
        productVariants.addEventListener('change', this.onProductOptionsChanged.bind(this));
        productVariants.addEventListener('input', this.onProductOptionsChanged.bind(this));
      }

      const masterSelector = this.productForm.querySelector('input[name="id"]');
      if (masterSelector) {
        masterSelector.addEventListener('change', () => {
          this.currentVariant = this.findVariantById(masterSelector.value);
          this.update();
        });
      }

      this.paymentButtonObserver = new MutationObserver(() => {
        this.updateDynamicCheckoutVisibility(this.isCustomVariantSelected());
      });
      this.paymentButtonObserver.observe(this.productForm, { childList: true, subtree: true });
    }

    getCurrentVariantId() {
      const input = this.productForm.querySelector('input[name="id"]');
      return input ? input.value : this.root.dataset.initialVariantId;
    }

    findVariantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    findVariantBySelectedOptions() {
      const selectedOptionValues = this.getSelectedOptionValues();
      if (!selectedOptionValues.length) return null;

      return (
        this.variants.find((variant) => {
          const variantOptions = Array.isArray(variant.options)
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter(Boolean);

          return selectedOptionValues.every((value, index) => String(variantOptions[index]) === String(value));
        }) || null
      );
    }

    onVariantChanged(event) {
      this.currentVariant = event.detail && event.detail.variant ? event.detail.variant : this.findVariantById(this.getCurrentVariantId());
      this.update();
    }

    onProductOptionsChanged() {
      this.currentVariant = this.findVariantBySelectedOptions() || this.findVariantById(this.getCurrentVariantId()) || this.currentVariant;
      this.update();
    }

    onDocumentKeyDown(event) {
      if (event.key !== 'Escape') return;

      if (this.seamModalOpen) {
        this.closeSeamModal();
        return;
      }

      if (this.cropModalOpen) {
        this.closeCropModal();
        return;
      }

      if (this.guideModalOpen) {
        this.closeGuideModal();
      }
    }

    onFormData(event) {
      if (!event.formData) return;

      event.formData.delete(cropImagePropertyName);
      const targetVariantSelected = this.isCustomVariantSelected();
      const customSizePricingSelected = this.isCustomSizePricingSelected();

      if (!customSizePricingSelected) {
        this.deleteCustomSizeFormData(event.formData);
      }

      const hasCustomDimensions = Boolean(
        event.formData.get('properties[Custom Width]') && event.formData.get('properties[Custom Height]')
      );
      const hasCustomerUpload =
        targetVariantSelected && this.isCustomerUploadMode() && this.uploadEnabled && this.uploadItems.length > 0;

      if (this.isCustomerUploadMode()) {
        for (let index = 0; index < maxCustomerUploadImages; index += 1) {
          event.formData.delete(getCustomerUploadPropertyName(index));
        }

        if (this.uploadEnabled) {
          this.uploadItems.slice(0, maxCustomerUploadImages).forEach((item, index) => {
            event.formData.append(getCustomerUploadPropertyName(index), item.file, item.file.name);
          });
        }

        if (hasCustomerUpload) {
          event.formData.set(cropPreferencePropertyName, this.getCropPreferenceValue());
        } else if (!hasCustomDimensions) {
          event.formData.delete(cropPreferencePropertyName);
        }

        return;
      }

      const hasCropImage = this.cropFile && targetVariantSelected;
      if (hasCropImage) {
        event.formData.append(cropImagePropertyName, this.cropFile, this.cropFile.name);
        event.formData.set(cropPreferencePropertyName, this.getCropPreferenceValue());
      }
      if (!hasCustomDimensions && !hasCropImage) {
        event.formData.delete(cropPreferencePropertyName);
      }
    }

    isCustomerUploadMode() {
      return this.customerUploadMode;
    }

    getProductVariantsElement() {
      const variants = Array.from(document.querySelectorAll('product-variants'));
      return (
        variants.find((element) => element.getAttribute('form-id') === this.formId && element.classList.contains('product-form__variants')) ||
        variants.find((element) => element.getAttribute('form-id') === this.formId)
      );
    }

    getSelectedOptionValues() {
      const productVariants = this.getProductVariantsElement();
      if (!productVariants) return [];

      const values = [];

      productVariants.querySelectorAll('select[name^="option"]').forEach((select) => {
        if (select.value) values.push(select.value);
      });

      productVariants.querySelectorAll('input[name^="option"]:checked').forEach((input) => {
        if (input.value) values.push(input.value);
      });

      return values.filter(Boolean);
    }

    getCurrentSizeOptionValue(variant = this.currentVariant) {
      if (!variant) return '';

      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter((option) => typeof option !== 'undefined');

      return options.length ? String(options[0] || '').trim() : '';
    }

    syncFixedVariantDimension(customSizeVisible) {
      if (!customSizeVisible) {
        this.fixedVariantDimension = null;
        this.lastProcessedSizeOptionValue = null;
        return null;
      }

      const sizeOptionValue = this.getCurrentSizeOptionValue();
      const variantTitle = String((this.currentVariant && this.currentVariant.title) || '').trim();
      const dimensionSourceSignature = `${sizeOptionValue}\n${variantTitle}`;
      if (this.lastProcessedSizeOptionValue === dimensionSourceSignature) {
        return this.fixedVariantDimension;
      }

      const hadPreviousSizeOption = this.lastProcessedSizeOptionValue !== null;
      const fixedVariantDimension =
        parseVariantDimension(sizeOptionValue) ||
        parseVariantDimension(variantTitle);

      this.lastProcessedSizeOptionValue = dimensionSourceSignature;
      this.fixedVariantDimension = fixedVariantDimension;

      if (hadPreviousSizeOption && !fixedVariantDimension) {
        this.widthInput.value = '';
        this.heightInput.value = '';
        this.unitSelect.value = this.config.defaultUnit || 'ft';
      }

      return fixedVariantDimension;
    }

    hasTargetVariantSku(variant = this.currentVariant) {
      const targetSkus = Array.isArray(this.config.targetVariantSkus) ? this.config.targetVariantSkus : [];
      const currentSku = variant && variant.sku ? String(variant.sku).trim().toUpperCase() : '';

      return targetSkus.some((sku) => currentSku && currentSku === String(sku).trim().toUpperCase());
    }

    isCustomVariantSelected() {
      return this.hasTargetVariantSku();
    }

    isFixedVariantSelected(variant = this.currentVariant) {
      if (!this.hasTargetVariantSku(variant)) return false;

      return Boolean(
        parseVariantDimension(this.getCurrentSizeOptionValue(variant)) ||
          parseVariantDimension(variant && variant.title)
      );
    }

    isCustomSizePricingSelected(variant = this.currentVariant) {
      return this.hasTargetVariantSku(variant) && !this.isFixedVariantSelected(variant);
    }

    setDimensionInputsState(options = {}) {
      const { editable = true, widthRaw = '', heightRaw = '', unit = this.config.defaultUnit || 'ft' } = options;

      if (this.widthInput) {
        this.widthInput.disabled = !editable;
        this.widthInput.readOnly = !editable;
        if (editable) {
          this.widthInput.removeAttribute('aria-readonly');
          this.widthInput.removeAttribute('aria-disabled');
        } else {
          this.widthInput.setAttribute('aria-readonly', 'true');
          this.widthInput.setAttribute('aria-disabled', 'true');
        }
        if (!editable || widthRaw !== '') {
          this.widthInput.value = widthRaw;
        }
      }

      if (this.heightInput) {
        this.heightInput.disabled = !editable;
        this.heightInput.readOnly = !editable;
        if (editable) {
          this.heightInput.removeAttribute('aria-readonly');
          this.heightInput.removeAttribute('aria-disabled');
        } else {
          this.heightInput.setAttribute('aria-readonly', 'true');
          this.heightInput.setAttribute('aria-disabled', 'true');
        }
        if (!editable || heightRaw !== '') {
          this.heightInput.value = heightRaw;
        }
      }

      if (this.unitSelect) {
        this.unitSelect.disabled = !editable;
        if (editable) {
          this.unitSelect.removeAttribute('aria-disabled');
        } else {
          this.unitSelect.setAttribute('aria-disabled', 'true');
        }
        if (!editable || unit) {
          this.unitSelect.value = unit;
        }
      }
    }

    getMaterialKey() {
      const currentSku = this.currentVariant && this.currentVariant.sku ? String(this.currentVariant.sku).trim().toUpperCase() : '';
      const materials = Array.isArray(this.config.materials) ? this.config.materials : [];
      const material = materials.find((rule) => currentSku && String(rule.sku || '').trim().toUpperCase() === currentSku);
      return material ? material.id : null;
    }

    getMaterialRule(materialKey = this.getMaterialKey()) {
      const materials = Array.isArray(this.config.materials) ? this.config.materials : [];
      return materialKey ? materials.find((material) => material.id === materialKey) || null : null;
    }

    getNormalizedDimensions(width, height, unit) {
      const feetToMeter = this.config.feetToMeter || 0.3048;

      if (unit === 'ft') {
        const widthM = ceilTo(width * feetToMeter, 2);
        const heightM = ceilTo(height * feetToMeter, 2);
        return {
          widthM,
          heightM,
          areaM2: roundTo(widthM * heightM, 2),
        };
      }

      if (unit === 'm') {
        return {
          widthM: width,
          heightM: height,
          areaM2: roundTo(width * height, 2),
        };
      }

      return null;
    }

    getDimensionsInFeet(width, height, unit) {
      const feetToMeter = Number(this.config.feetToMeter || 0.3048);

      if (unit === 'ft') {
        return {
          widthFt: width,
          heightFt: height,
        };
      }

      if (unit === 'm' && feetToMeter > 0) {
        return {
          widthFt: width / feetToMeter,
          heightFt: height / feetToMeter,
        };
      }

      return null;
    }

    calculatePrice(dimensions, materialRule) {
      const rawPrice =
        dimensions.areaM2 * Number(materialRule.pricePerSquareMeter) + Number(materialRule.fixedFee);
      const minimumPrice = Number(materialRule.minimumPrice || 0);
      return rawPrice < minimumPrice ? roundTo(minimumPrice, 2) : Math.round(rawPrice);
    }

    validate(widthRaw, heightRaw, unit, materialRule, dimensions) {
      if (!materialRule) return this.getMessage('unsupportedMaterial', 'Selected material is not available for custom size pricing.');
      if (!widthRaw || !heightRaw) return this.getMessage('invalidDimensions', 'Please enter a valid custom width and height.');
      if (!hasMaxTwoDecimals(widthRaw) || !hasMaxTwoDecimals(heightRaw)) return this.getMessage('tooManyDecimals', 'Width and height can use up to 2 decimal places.');
      if (!this.config.allowedUnits || !this.config.allowedUnits.includes(unit)) return this.getMessage('invalidUnit', 'Please select a valid unit.');
      if (!dimensions || dimensions.widthM <= 0 || dimensions.heightM <= 0) return this.getMessage('invalidDimensions', 'Please enter a valid custom width and height.');
      return '';
    }

    clearHiddenFields() {
      Object.keys(this.hiddenFields).forEach((key) => {
        if (this.hiddenFields[key]) {
          this.hiddenFields[key].value = '';
          this.hiddenFields[key].disabled = true;
        }
      });
    }

    deleteCustomSizeFormData(formData) {
      customSizePropertyNames.forEach((propertyName) => {
        formData.delete(propertyName);
      });
    }

    updateHiddenFields(widthRaw, heightRaw, unit, materialRule, dimensions, price) {
      Object.keys(this.hiddenFields).forEach((key) => {
        if (this.hiddenFields[key]) {
          this.hiddenFields[key].disabled = false;
        }
      });
      this.hiddenFields.flag.value = '1';
      this.hiddenFields.rule.value = this.config.version || '';
      this.hiddenFields.width.value = `${widthRaw} ${unit}`;
      this.hiddenFields.height.value = `${heightRaw} ${unit}`;
      this.hiddenFields.unit.value = unit;
      this.hiddenFields.crop.value = this.getCropPreferenceValue();
      this.hiddenFields.price.value = price.toFixed(2);
    }

    setMessage(text) {
      this.message.textContent = text || '';
      this.message.hidden = !text;
    }

    setWarning(text) {
      this.warning.textContent = text || '';
      this.warning.hidden = !text;
    }

    getSeamNotice(materialKey) {
      const seamNotices = this.config && this.config.seamNotices ? this.config.seamNotices : {};
      return materialKey && seamNotices[materialKey] ? seamNotices[materialKey] : null;
    }

    shouldShowSeamNotice(materialKey, width, height, unit) {
      const notice = this.getSeamNotice(materialKey);
      const thresholdFt = notice ? Number(notice.thresholdFt) : null;
      const dimensionsFt = this.getDimensionsInFeet(width, height, unit);

      return Boolean(
        notice &&
          Number.isFinite(thresholdFt) &&
          thresholdFt > 0 &&
          dimensionsFt &&
          dimensionsFt.widthFt > thresholdFt &&
          dimensionsFt.heightFt > thresholdFt
      );
    }

    updateSeamNotice(materialKey) {
      const notice = this.getSeamNotice(materialKey);
      const imageUrl = notice ? normalizeImageUrl(notice.imageUrl || notice.image || '') : '';
      const imageAlt = notice && notice.alt ? notice.alt : 'Stitching seam example';
      const imageWidth = notice && notice.imageWidth ? String(notice.imageWidth) : '';
      const imageHeight = notice && notice.imageHeight ? String(notice.imageHeight) : '';

      this.currentSeamNotice = notice;

      if (this.seamNotice) {
        this.seamNotice.hidden = !notice;
      }

      if (this.seamText) {
        this.seamText.textContent = notice && notice.text ? String(notice.text).replace(/\\n/g, '\n') : '';
      }

      if (this.seamOpenButton) {
        this.seamOpenButton.hidden = !imageUrl;
        this.seamOpenButton.setAttribute('aria-expanded', this.seamModalOpen ? 'true' : 'false');
      }

      if (this.seamThumbnail) {
        if (imageUrl) {
          this.seamThumbnail.src = imageUrl;
        } else {
          this.seamThumbnail.removeAttribute('src');
        }
        this.seamThumbnail.alt = imageAlt;
        if (imageWidth && imageHeight) {
          this.seamThumbnail.setAttribute('width', imageWidth);
          this.seamThumbnail.setAttribute('height', imageHeight);
        }
      }

      if (this.seamModalImage) {
        if (imageUrl) {
          this.seamModalImage.src = imageUrl;
        } else {
          this.seamModalImage.removeAttribute('src');
        }
        this.seamModalImage.alt = imageAlt;
        if (imageWidth && imageHeight) {
          this.seamModalImage.setAttribute('width', imageWidth);
          this.seamModalImage.setAttribute('height', imageHeight);
        }
      }

      if (!notice && this.seamModalOpen) {
        this.closeSeamModal();
      }
    }

    openSeamModal() {
      if (!this.currentSeamNotice || !this.seamModal) return;

      const imageUrl = normalizeImageUrl(this.currentSeamNotice.imageUrl || this.currentSeamNotice.image || '');
      if (!imageUrl) return;

      if (this.seamModalImage) {
        this.seamModalImage.src = imageUrl;
        this.seamModalImage.alt = this.currentSeamNotice.alt || 'Stitching seam example';
        if (this.currentSeamNotice.imageWidth && this.currentSeamNotice.imageHeight) {
          this.seamModalImage.setAttribute('width', String(this.currentSeamNotice.imageWidth));
          this.seamModalImage.setAttribute('height', String(this.currentSeamNotice.imageHeight));
        }
      }

      this.seamModal.hidden = false;
      this.seamModalOpen = true;

      if (this.seamOpenButton) {
        this.seamOpenButton.setAttribute('aria-expanded', 'true');
      }
    }

    closeSeamModal() {
      if (!this.seamModal) return;

      this.seamModal.hidden = true;
      this.seamModalOpen = false;

      if (this.seamOpenButton) {
        this.seamOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    openGuideModal() {
      if (!this.guideModal) return;

      this.guideModal.hidden = false;
      this.guideModalOpen = true;
      document.documentElement.classList.add('custom-size-pricing-guide-open');

      if (this.guideOpenButton) {
        this.guideOpenButton.setAttribute('aria-expanded', 'true');
      }
    }

    closeGuideModal() {
      if (!this.guideModal) return;

      this.guideModal.hidden = true;
      this.guideModalOpen = false;
      document.documentElement.classList.remove('custom-size-pricing-guide-open');

      if (this.guideOpenButton) {
        this.guideOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    updateAddToCartButton(disabled) {
      const buttons = this.productForm.querySelectorAll('button[type="submit"], [type="submit"][data-product-add-to-cart-button]');
      buttons.forEach((button) => {
        if (button.closest('.shopify-payment-button')) return;

        if (disabled) {
          if (!button.disabled) {
            button.dataset.customSizeDisabled = 'true';
            button.setAttribute('disabled', 'disabled');
          }
        } else if (button.dataset.customSizeDisabled === 'true') {
          button.removeAttribute('disabled');
          delete button.dataset.customSizeDisabled;
        }
      });
    }

    updateDynamicCheckoutVisibility(hidden) {
      const paymentButtons = this.productForm.querySelectorAll('.shopify-payment-button');
      paymentButtons.forEach((element) => {
        if (hidden) {
          if (element.dataset.customSizeHidden !== 'true') {
            element.dataset.customSizePreviousDisplay = element.style.display || '';
            element.dataset.customSizeHidden = 'true';
          }
          element.style.display = 'none';
        } else if (element.dataset.customSizeHidden === 'true') {
          element.style.display = element.dataset.customSizePreviousDisplay || '';
          delete element.dataset.customSizePreviousDisplay;
          delete element.dataset.customSizeHidden;
        }
      });
    }

    updatePriceDisplays(formattedPrice) {
      const salePriceLabel = window.themeVariables && window.themeVariables.strings ? window.themeVariables.strings.productSalePrice : 'Sale price';
      const priceHtml = `<span class="price price--large"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>`;

      document.querySelectorAll(`product-meta[form-id="${this.formId}"] [data-product-price-list]`).forEach((priceList) => {
        priceList.innerHTML = priceHtml;
        priceList.style.display = '';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = formattedPrice;
      });
    }

    restoreVariantPriceDisplays() {
      if (this.priceOutput) {
        this.priceOutput.innerHTML = '';
      }

      const variant = this.currentVariant;
      const variantPrice = variant && typeof variant.price !== 'undefined' ? toNumber(variant.price) : null;
      if (!variant || variantPrice === null) {
        this.clearPriceDisplays();
        return;
      }

      const price = Math.round(variantPrice);
      const compareAtPrice =
        typeof variant.compare_at_price !== 'undefined' && variant.compare_at_price !== null
          ? toNumber(variant.compare_at_price)
          : null;
      const formattedPrice = formatMoney(price);
      const salePriceLabel =
        window.themeVariables && window.themeVariables.strings
          ? window.themeVariables.strings.productSalePrice
          : 'Sale price';
      const regularPriceLabel =
        window.themeVariables && window.themeVariables.strings
          ? window.themeVariables.strings.productRegularPrice
          : 'Regular price';

      document.querySelectorAll(`product-meta[form-id="${this.formId}"]`).forEach((productMeta) => {
        const priceList = productMeta.querySelector('[data-product-price-list]');
        if (!priceList) return;

        const priceClass = productMeta.getAttribute('price-class') || '';
        if (compareAtPrice !== null && compareAtPrice > price) {
          priceList.innerHTML =
            `<span class="price price--highlight ${priceClass}"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>` +
            `<span class="price price--compare"><span class="visually-hidden">${regularPriceLabel}</span>${formatMoney(
              Math.round(compareAtPrice)
            )}</span>`;
        } else {
          priceList.innerHTML = `<span class="price ${priceClass}"><span class="visually-hidden">${salePriceLabel}</span>${formattedPrice}</span>`;
        }

        const unitPrice = typeof variant.unit_price !== 'undefined' ? toNumber(variant.unit_price) : null;
        if (variant.unit_price_measurement && unitPrice !== null) {
          const measurement = variant.unit_price_measurement;
          const referenceValue =
            measurement.reference_value !== 1
              ? `<span class="unit-price-measurement__reference-value">${measurement.reference_value}</span>`
              : '';
          priceList.innerHTML += `
            <div class="price text--subdued ${productMeta.getAttribute('unit-price-class') || ''}">
              <div class="unit-price-measurement">
                <span class="unit-price-measurement__price">${formatMoney(Math.round(unitPrice))}</span>
                <span class="unit-price-measurement__separator">/</span>
                ${referenceValue}
                <span class="unit-price-measurement__reference-unit">${measurement.reference_unit}</span>
              </div>
            </div>
          `;
        }

        priceList.style.display = '';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = formattedPrice;
      });
    }

    clearPriceDisplays() {
      if (this.priceOutput) {
        this.priceOutput.innerHTML = '';
      }

      document.querySelectorAll(`product-meta[form-id="${this.formId}"] [data-product-price-list]`).forEach((priceList) => {
        priceList.innerHTML = '';
        priceList.style.display = 'none';
      });

      document.querySelectorAll(`product-sticky-form[form-id="${this.formId}"] .product-sticky-form__price`).forEach((priceElement) => {
        priceElement.innerHTML = '';
      });
    }

    getDimensionSignature(widthRaw, heightRaw, unit) {
      return `${widthRaw}|${heightRaw}|${unit}`;
    }

    formatCropDimensionValue(value) {
      const number = toNumber(value);
      if (number === null) return '';

      const rounded = roundTo(number, 2);
      return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
    }

    getCropDimensionLabel(widthRaw, heightRaw, unit) {
      const widthLabel = this.formatCropDimensionValue(widthRaw);
      const heightLabel = this.formatCropDimensionValue(heightRaw);
      const unitLabel = unit ? String(unit).trim() : '';

      return widthLabel && heightLabel && unitLabel ? `${widthLabel}*${heightLabel}${unitLabel}` : '';
    }

    setCropDimensionLabel(widthRaw, heightRaw, unit) {
      this.currentCropDimensionLabel = this.getCropDimensionLabel(widthRaw, heightRaw, unit);

      if (this.cropModalOpen) {
        this.drawCropCanvas();
      }
    }

    clearCropDimensionLabel() {
      this.currentCropDimensionLabel = '';

      if (this.cropModalOpen) {
        this.drawCropCanvas();
      }
    }

    setCropPreference(value) {
      if (this.hiddenFields.crop) {
        this.hiddenFields.crop.value = value;
      }
    }

    getCropPreferenceValue() {
      if (this.isCustomerUploadMode()) {
        if (!this.uploadItems.length) return this.getMessage('designerAdjust', 'Designer adjust');
        return this.uploadItems.some((item) => item.cropped)
          ? this.getMessage('customerCrop', 'Customer crop')
          : this.getMessage('customerUpload', 'Customer upload');
      }

      return this.cropFile ? this.getMessage('customerCrop', 'Customer crop') : this.getMessage('designerAdjust', 'Designer adjust');
    }

    clearCropSelection(options = {}) {
      if (this.isCustomerUploadMode()) {
        this.resetCropGestureState();
        if (options.updateUi !== false) {
          this.updateUploadUi();
        }
        return;
      }

      const updateUi = options.updateUi !== false;
      this.resetCropGestureState();
      this.cropFile = null;
      this.revokeCropPreviewUrl();
      this.setCropPreference(options.resetPreference === false ? '' : this.getMessage('designerAdjust', 'Designer adjust'));
      this.setCropFileInput(null);

      if (updateUi) {
        this.updateCropStatus();
      }
    }

    revokeCropPreviewUrl() {
      if (this.cropPreviewUrl) {
        URL.revokeObjectURL(this.cropPreviewUrl);
        this.cropPreviewUrl = '';
      }

      if (this.cropPreviewImage) {
        this.cropPreviewImage.removeAttribute('src');
      }
    }

    setCropPreviewFile(file) {
      this.revokeCropPreviewUrl();

      if (!file || !this.cropPreviewImage) return;

      this.cropPreviewUrl = URL.createObjectURL(file);
      this.cropPreviewImage.src = this.cropPreviewUrl;
    }

    setCropFileInput(file) {
      if (!this.cropFileInput) return;

      this.cropFileInput.removeAttribute('name');
      this.cropFileInput.value = '';

      if (!file || typeof DataTransfer === 'undefined') return;

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.cropFileInput.files = transfer.files;
        this.cropFileInput.setAttribute('name', cropImagePropertyName);
      } catch (error) {
        this.cropFileInput.removeAttribute('name');
        this.cropFileInput.value = '';
      }
    }

    updateCropStatus() {
      if (this.cropStatus) {
        const statusText = this.cropFile ? 'Crop completed' : '';
        this.cropStatus.textContent = statusText;
        this.cropStatus.hidden = !statusText;
      }

      if (this.cropOpenButton) {
        this.cropOpenButton.hidden = Boolean(this.cropFile);
      }

      if (this.cropPreview) {
        this.cropPreview.hidden = !this.cropFile;
      }
    }

    onUploadInputChange(event) {
      const files = Array.from(event.target.files || []);
      const availableSlots = Math.max(0, maxCustomerUploadImages - this.uploadItems.length);
      const acceptedFiles = files.slice(0, availableSlots);

      acceptedFiles.forEach((file) => this.addCustomerUpload(file));

      if (files.length > availableSlots) {
        this.setWarning('You can upload up to 3 images.');
      }

      if (this.uploadInput) {
        this.uploadInput.value = '';
      }
    }

    isAcceptedCustomerUpload(file) {
      if (!file) return false;
      if (customerUploadTypes.includes(file.type)) return true;

      return /\.(jpe?g|png|webp)$/i.test(file.name || '');
    }

    addCustomerUpload(file) {
      if (!this.isAcceptedCustomerUpload(file)) {
        this.setWarning('Please upload a JPG, PNG, or WEBP image.');
        return;
      }

      if (this.uploadItems.length >= maxCustomerUploadImages) {
        this.setWarning('You can upload up to 3 images.');
        return;
      }

      this.uploadItems.push({
        file,
        previewUrl: URL.createObjectURL(file),
        cropped: false,
      });
      this.setWarning('');
      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
    }

    renderCustomerUploads() {
      if (!this.isCustomerUploadMode() || !this.uploadList) return;

      this.uploadList.innerHTML = '';
      this.uploadItems.forEach((item, index) => {
        const wrapper = document.createElement('div');
        const cropButton = document.createElement('button');
        const image = document.createElement('img');
        const removeButton = document.createElement('button');

        wrapper.className = 'custom-size-pricing__upload-item';
        cropButton.type = 'button';
        cropButton.className = 'custom-size-pricing__upload-thumb';
        cropButton.setAttribute('aria-label', `Crop uploaded image ${index + 1}`);
        cropButton.addEventListener('click', () => this.openUploadCropModal(index));

        image.src = item.previewUrl;
        image.alt = item.file && item.file.name ? item.file.name : `Uploaded image ${index + 1}`;
        image.loading = 'lazy';

        removeButton.type = 'button';
        removeButton.className = 'custom-size-pricing__upload-remove';
        removeButton.setAttribute('aria-label', `Remove uploaded image ${index + 1}`);
        removeButton.innerHTML = '&times;';
        removeButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.removeCustomerUpload(index);
        });

        cropButton.appendChild(image);
        wrapper.appendChild(cropButton);
        wrapper.appendChild(removeButton);
        this.uploadList.appendChild(wrapper);
      });

      this.updateUploadUi();
    }

    removeCustomerUpload(index) {
      const item = this.uploadItems[index];
      if (!item) return;

      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      this.uploadItems.splice(index, 1);
      if (this.activeCropImageIndex >= this.uploadItems.length) {
        this.activeCropImageIndex = Math.max(0, this.uploadItems.length - 1);
      }

      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
      this.setCropPreference(this.getCropPreferenceValue());
    }

    replaceCustomerUploadWithCrop(file) {
      const item = this.uploadItems[this.activeCropImageIndex];
      if (!item) return;

      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      item.file = file;
      item.previewUrl = URL.createObjectURL(file);
      item.cropped = true;
      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
      this.setCropPreference(this.getCropPreferenceValue());
    }

    clearCustomerUploads() {
      if (!this.isCustomerUploadMode()) return;

      this.uploadItems.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      this.uploadItems = [];

      if (this.uploadInput) {
        this.uploadInput.value = '';
      }

      this.renderCustomerUploads();
      this.syncCustomerUploadFileInputs();
    }

    updateUploadUi() {
      if (!this.isCustomerUploadMode()) return;

      const showUploadButton = this.uploadEnabled && this.uploadItems.length < maxCustomerUploadImages;

      if (this.uploadButton) {
        this.uploadButton.hidden = !showUploadButton;
      }

      if (this.uploadList) {
        this.uploadList.hidden = !this.uploadItems.length;
      }

      this.syncCustomerUploadFileInputs();
    }

    syncCustomerUploadFileInputs() {
      if (!this.uploadFileInputs || !this.uploadFileInputs.length) return;

      this.uploadFileInputs.forEach((input, index) => {
        input.removeAttribute('name');
        input.value = '';

        const item = this.uploadItems[index];
        if (!this.uploadEnabled || !item || !item.file || typeof DataTransfer === 'undefined') return;

        try {
          const transfer = new DataTransfer();
          transfer.items.add(item.file);
          input.files = transfer.files;
          input.setAttribute('name', getCustomerUploadPropertyName(index));
        } catch (error) {
          input.removeAttribute('name');
          input.value = '';
        }
      });
    }

    updateCropUi(canCrop) {
      if (this.isCustomerUploadMode()) {
        this.uploadEnabled = Boolean(canCrop && this.cropAspectRatio);

        if (this.uploadSection) {
          this.uploadSection.hidden = !this.uploadEnabled;
        }

        if (this.uploadHint) {
          this.uploadHint.hidden = !this.uploadEnabled;
        }

        if (!this.uploadEnabled && this.cropModalOpen) {
          this.closeCropModal();
        }

        this.updateUploadUi();
        return;
      }

      const enabled = Boolean(canCrop && this.images.length > 0 && this.cropAspectRatio);

      if (this.cropSection) {
        this.cropSection.hidden = !enabled;
      }

      if (this.cropOpenButton) {
        this.cropOpenButton.disabled = !enabled;
        this.cropOpenButton.setAttribute('aria-expanded', this.cropModalOpen ? 'true' : 'false');
      }

      if (!enabled && this.cropModalOpen) {
        this.closeCropModal();
      }

      this.updateCropStatus();
    }

    renderCropThumbnails() {
      if (!this.cropThumbs) return;

      if (this.isCustomerUploadMode()) {
        this.cropThumbs.hidden = true;
        return;
      }

      this.cropThumbs.innerHTML = '';
      this.images.forEach((image, index) => {
        const button = document.createElement('button');
        const thumbnail = document.createElement('img');

        button.type = 'button';
        button.className = 'custom-size-pricing__crop-thumb';
        button.setAttribute('data-custom-size-crop-thumb', '');
        button.setAttribute('aria-label', `Use image ${index + 1}`);
        button.addEventListener('click', () => this.selectCropImage(index, { clearSelection: true }));

        thumbnail.src = normalizeImageUrl(image.previewSrc || image.src);
        thumbnail.alt = image.alt || '';
        thumbnail.loading = 'lazy';
        button.appendChild(thumbnail);
        this.cropThumbs.appendChild(button);
      });

      this.updateCropThumbSelection();
    }

    updateCropThumbSelection() {
      if (!this.cropThumbs) return;
      if (this.isCustomerUploadMode()) return;

      this.cropThumbs.querySelectorAll('[data-custom-size-crop-thumb]').forEach((button, index) => {
        const selected = index === this.activeCropImageIndex;
        button.classList.toggle('is-selected', selected);
        button.toggleAttribute('aria-current', selected);
      });
    }

    openCropModal() {
      if (this.isCustomerUploadMode()) {
        this.openUploadCropModal(this.activeCropImageIndex);
        return;
      }

      if (!this.cropAspectRatio || !this.images.length || !this.cropModal) return;

      this.cropModal.hidden = false;
      this.cropModalOpen = true;

      if (this.cropOpenButton) {
        this.cropOpenButton.setAttribute('aria-expanded', 'true');
      }

      this.setCropModalMessage('');
      this.updateCropThumbSelection();
      requestAnimationFrame(() => {
        this.selectCropImage(this.activeCropImageIndex, { clearSelection: false });
      });
    }

    openUploadCropModal(index) {
      if (!this.uploadEnabled || !this.uploadItems[index] || !this.cropAspectRatio || !this.cropModal) return;

      this.activeCropImageIndex = index;
      this.cropModal.hidden = false;
      this.cropModalOpen = true;
      this.setCropModalMessage('');

      requestAnimationFrame(() => {
        this.selectCropImage(index, { clearSelection: false });
      });
    }

    closeCropModal() {
      if (!this.cropModal) return;

      this.cropModal.hidden = true;
      this.cropModalOpen = false;
      this.resetCropGestureState();

      if (this.cropOpenButton) {
        this.cropOpenButton.setAttribute('aria-expanded', 'false');
      }
    }

    setCropModalMessage(text) {
      if (!this.cropModalMessage) return;

      this.cropModalMessage.textContent = text || '';
      this.cropModalMessage.hidden = !text;
    }

    async selectCropImage(index, options = {}) {
      const imageData = this.getCropImageData(index);
      if (!imageData) return;

      this.resetCropGestureState();

      if (options.clearSelection) {
        this.clearCropSelection();
      }

      this.activeCropImageIndex = index;
      this.updateCropThumbSelection();

      const loadId = ++this.cropImageLoadingId;

      try {
        const image = await loadImage(imageData.src);
        if (loadId !== this.cropImageLoadingId) return;

        this.cropImageElement = image;
        this.cropRect = null;
        this.setCropModalMessage('');
        this.resizeCropCanvas();
      } catch (error) {
        if (loadId !== this.cropImageLoadingId) return;
        this.cropImageElement = null;
        this.cropRect = null;
        this.drawEmptyCropCanvas();
        this.setCropModalMessage(this.getMessage('unableToLoadImage', 'Unable to load this image.'));
      }
    }

    getCropImageData(index) {
      if (this.isCustomerUploadMode()) {
        const item = this.uploadItems[index];
        if (!item || !item.previewUrl) return null;

        return {
          src: item.previewUrl,
          previewSrc: item.previewUrl,
          alt: item.file && item.file.name ? item.file.name : `Uploaded image ${index + 1}`,
        };
      }

      return this.images[index] || null;
    }

    resizeCropCanvas() {
      if (!this.cropModalOpen || !this.cropCanvas || !this.cropCanvasContext) return;

      const stage = this.cropCanvas.parentElement;
      const cssWidth = Math.max(280, Math.floor(stage ? stage.clientWidth : 640));
      const cssHeightRatio = window.matchMedia('(max-width: 740px)').matches ? 0.86 : 0.64;
      const cssHeight = Math.max(260, Math.min(620, Math.round(cssWidth * cssHeightRatio)));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const canvasWidth = Math.round(cssWidth * pixelRatio);
      const canvasHeight = Math.round(cssHeight * pixelRatio);

      if (this.cropCanvas.width !== canvasWidth || this.cropCanvas.height !== canvasHeight) {
        this.cropCanvas.width = canvasWidth;
        this.cropCanvas.height = canvasHeight;
        this.cropCanvas.style.width = `${cssWidth}px`;
        this.cropCanvas.style.height = `${cssHeight}px`;
      }

      this.cropCanvasContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.cropCanvasSize = { width: cssWidth, height: cssHeight };

      if (!this.cropImageElement) {
        this.drawEmptyCropCanvas();
        return;
      }

      this.resetCropGestureState();
      this.layoutCropCanvas(true);
      this.drawCropCanvas();
    }

    drawEmptyCropCanvas() {
      if (!this.cropCanvasContext) return;

      this.cropCanvasContext.clearRect(0, 0, this.cropCanvasSize.width, this.cropCanvasSize.height);
      this.cropCanvasContext.fillStyle = '#f6f6f6';
      this.cropCanvasContext.fillRect(0, 0, this.cropCanvasSize.width, this.cropCanvasSize.height);
    }

    fitSize(maxWidth, maxHeight, aspectRatio) {
      let width = maxWidth;
      let height = width / aspectRatio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      return { width, height };
    }

    layoutCropCanvas(resetCropRect) {
      const imageAspectRatio = this.cropImageElement.naturalWidth / this.cropImageElement.naturalHeight;
      const imageSize = this.fitSize(this.cropCanvasSize.width * 0.96, this.cropCanvasSize.height * 0.96, imageAspectRatio);
      this.cropImageRect = {
        x: (this.cropCanvasSize.width - imageSize.width) / 2,
        y: (this.cropCanvasSize.height - imageSize.height) / 2,
        width: imageSize.width,
        height: imageSize.height,
      };

      if (resetCropRect || !this.cropRect) {
        this.cropRect = this.getDefaultCropRect();
      } else {
        this.cropRect = this.clampCropRect(this.cropRect);
      }
    }

    getDefaultCropRect() {
      const size = this.getMaxCropSize();

      return {
        x: this.cropImageRect.x + (this.cropImageRect.width - size.width) / 2,
        y: this.cropImageRect.y + (this.cropImageRect.height - size.height) / 2,
        width: size.width,
        height: size.height,
      };
    }

    getMaxCropSize() {
      return this.fitSize(this.cropImageRect.width, this.cropImageRect.height, this.cropAspectRatio);
    }

    getMinCropWidth(maxSize = this.getMaxCropSize()) {
      return Math.min(maxSize.width, Math.max(48, maxSize.width * 0.15));
    }

    clampCropRect(rect) {
      const maxSize = this.getMaxCropSize();
      let width = Math.min(rect.width, maxSize.width);
      let height = width / this.cropAspectRatio;

      if (height > maxSize.height) {
        height = maxSize.height;
        width = height * this.cropAspectRatio;
      }

      return {
        x: clamp(rect.x, this.cropImageRect.x, this.cropImageRect.x + this.cropImageRect.width - width),
        y: clamp(rect.y, this.cropImageRect.y, this.cropImageRect.y + this.cropImageRect.height - height),
        width,
        height,
      };
    }

    scaleCropRect(factor, center, baseRect = this.cropRect) {
      if (!baseRect || !center || !Number.isFinite(factor) || factor <= 0) return;

      const maxSize = this.getMaxCropSize();
      const minWidth = this.getMinCropWidth(maxSize);
      const minHeight = minWidth / this.cropAspectRatio;
      let width = clamp(baseRect.width * factor, minWidth, maxSize.width);
      let height = width / this.cropAspectRatio;

      if (height > maxSize.height) {
        height = maxSize.height;
        width = height * this.cropAspectRatio;
      } else if (height < minHeight) {
        height = minHeight;
        width = height * this.cropAspectRatio;
      }

      this.cropRect = this.clampCropRect({
        x: center.x - width / 2,
        y: center.y - height / 2,
        width,
        height,
      });
      this.drawCropCanvas();
    }

    drawCropCanvas() {
      if (!this.cropCanvasContext || !this.cropImageElement || !this.cropImageRect || !this.cropRect) return;

      const ctx = this.cropCanvasContext;
      const canvasWidth = this.cropCanvasSize.width;
      const canvasHeight = this.cropCanvasSize.height;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = '#f6f6f6';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(
        this.cropImageElement,
        this.cropImageRect.x,
        this.cropImageRect.y,
        this.cropImageRect.width,
        this.cropImageRect.height
      );

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, canvasWidth, canvasHeight);
      ctx.rect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.52)';
      ctx.fill('evenodd');
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 5;
      ctx.strokeRect(this.cropRect.x, this.cropRect.y, this.cropRect.width, this.cropRect.height);
      ctx.restore();

      this.drawCropDimensionLabel();
    }

    drawCropDimensionLabel() {
      const label = this.currentCropDimensionLabel;
      if (!label || !this.cropCanvasContext || !this.cropRect) return;

      const ctx = this.cropCanvasContext;
      const canvasWidth = this.cropCanvasSize.width;
      const canvasHeight = this.cropCanvasSize.height;
      const fontSize = 14;
      const lineHeight = 18;
      const gap = 8;
      const rightEdge = this.cropRect.x + this.cropRect.width;
      const topEdge = this.cropRect.y;
      const bottomEdge = this.cropRect.y + this.cropRect.height;

      ctx.save();
      ctx.font = `700 ${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'bottom';
      if ('textRendering' in ctx) {
        ctx.textRendering = 'geometricPrecision';
      }

      const textWidth = ctx.measureText(label).width;
      const canDrawAbove = topEdge - gap - lineHeight >= 0;
      const canDrawRight = rightEdge + gap + textWidth <= canvasWidth;
      const canDrawBelow = bottomEdge + gap + lineHeight <= canvasHeight;
      const canDrawLeft = this.cropRect.x - gap - textWidth >= 0;
      const alignTextPosition = (value) => Math.round(value);

      if (canDrawAbove) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(topEdge - gap);
        ctx.fillText(label, x, y);
      } else if (canDrawRight) {
        ctx.textAlign = 'left';
        const x = alignTextPosition(rightEdge + gap);
        const y = alignTextPosition(clamp(topEdge + lineHeight, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      } else if (canDrawBelow) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(bottomEdge + gap + lineHeight);
        ctx.fillText(label, x, y);
      } else if (canDrawLeft) {
        ctx.textAlign = 'right';
        const x = alignTextPosition(this.cropRect.x - gap);
        const y = alignTextPosition(clamp(topEdge + lineHeight, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      } else {
        ctx.textAlign = 'right';
        const x = alignTextPosition(clamp(rightEdge, textWidth, canvasWidth));
        const y = alignTextPosition(clamp(topEdge - gap, lineHeight, canvasHeight));
        ctx.fillText(label, x, y);
      }

      ctx.restore();
    }

    getCanvasPoint(event) {
      const rect = this.cropCanvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    }

    getActiveCropPointerEntries() {
      return Array.from(this.cropPointers.entries()).slice(0, 2);
    }

    getCropPinchSnapshot() {
      const entries = this.getActiveCropPointerEntries();
      if (entries.length < 2) return null;

      const pointA = entries[0][1];
      const pointB = entries[1][1];
      return {
        distance: Math.max(1, getDistance(pointA, pointB)),
        center: getMidpoint(pointA, pointB),
      };
    }

    cropRectContains(point) {
      return (
        this.cropRect &&
        point.x >= this.cropRect.x &&
        point.x <= this.cropRect.x + this.cropRect.width &&
        point.y >= this.cropRect.y &&
        point.y <= this.cropRect.y + this.cropRect.height
      );
    }

    resetCropGestureState() {
      if (this.cropCanvas && this.cropPointers) {
        this.cropPointers.forEach((_, pointerId) => {
          this.releaseCropPointer(pointerId);
        });
        this.cropCanvas.classList.remove('is-dragging');
      }

      if (this.cropPointers) {
        this.cropPointers.clear();
      }
      this.cropDrag = null;
      this.cropPinch = null;
    }

    captureCropPointer(pointerId) {
      if (!this.cropCanvas || !this.cropCanvas.setPointerCapture) return;

      try {
        this.cropCanvas.setPointerCapture(pointerId);
      } catch (error) {
        // Synthetic pointer events and some older touch browsers can reject capture.
      }
    }

    releaseCropPointer(pointerId) {
      if (!this.cropCanvas || !this.cropCanvas.hasPointerCapture || !this.cropCanvas.releasePointerCapture) return;

      try {
        if (this.cropCanvas.hasPointerCapture(pointerId)) {
          this.cropCanvas.releasePointerCapture(pointerId);
        }
      } catch (error) {
        // Pointer capture may already be gone after touch cancellation or modal close.
      }
    }

    startCropDrag(pointerId, point) {
      if (!this.cropRectContains(point)) return;

      this.cropDrag = {
        pointerId,
        offsetX: point.x - this.cropRect.x,
        offsetY: point.y - this.cropRect.y,
      };
      this.cropCanvas.classList.add('is-dragging');
    }

    stopCropDrag() {
      this.cropDrag = null;
      if (this.cropCanvas) {
        this.cropCanvas.classList.remove('is-dragging');
      }
    }

    startCropPinch() {
      const snapshot = this.getCropPinchSnapshot();
      if (!snapshot || !this.cropRect) return;

      this.stopCropDrag();
      this.cropPinch = {
        initialDistance: snapshot.distance,
        initialRect: { ...this.cropRect },
      };
    }

    updateCropPinch() {
      if (!this.cropPinch) return;

      const snapshot = this.getCropPinchSnapshot();
      if (!snapshot) return;

      const factor = snapshot.distance / this.cropPinch.initialDistance;
      this.scaleCropRect(factor, snapshot.center, this.cropPinch.initialRect);
    }

    resumeCropDragFromActivePointer() {
      if (this.cropPointers.size !== 1) return;

      const entry = Array.from(this.cropPointers.entries())[0];
      if (!entry) return;

      const [pointerId, point] = entry;
      this.startCropDrag(pointerId, point);
    }

    onCropPointerDown(event) {
      if (!this.cropModalOpen || !this.cropRect || !this.cropImageRect) return;

      const point = this.getCanvasPoint(event);
      event.preventDefault();
      this.cropPointers.set(event.pointerId, point);
      this.captureCropPointer(event.pointerId);

      if (this.cropPointers.size >= 2) {
        this.startCropPinch();
        return;
      }

      this.startCropDrag(event.pointerId, point);
    }

    onCropPointerMove(event) {
      if (!this.cropRect || !this.cropImageRect || !this.cropPointers.has(event.pointerId)) return;

      event.preventDefault();
      const point = this.getCanvasPoint(event);
      this.cropPointers.set(event.pointerId, point);

      if (this.cropPointers.size >= 2) {
        if (!this.cropPinch) {
          this.startCropPinch();
        }
        this.updateCropPinch();
        return;
      }

      if (!this.cropDrag || this.cropDrag.pointerId !== event.pointerId) return;

      this.cropRect = this.clampCropRect({
        x: point.x - this.cropDrag.offsetX,
        y: point.y - this.cropDrag.offsetY,
        width: this.cropRect.width,
        height: this.cropRect.height,
      });
      this.drawCropCanvas();
    }

    onCropPointerUp(event) {
      const wasPinching = Boolean(this.cropPinch);
      this.cropPointers.delete(event.pointerId);

      if (this.cropDrag && this.cropDrag.pointerId === event.pointerId) {
        this.stopCropDrag();
      }

      this.releaseCropPointer(event.pointerId);

      if (this.cropPointers.size >= 2) {
        this.startCropPinch();
      } else if (wasPinching) {
        this.cropPinch = null;
        this.stopCropDrag();
        this.resumeCropDragFromActivePointer();
      } else {
        this.cropPinch = null;
      }
    }

    onCropWheel(event) {
      if (!this.cropModalOpen || !this.cropRect || !this.cropImageRect) return;

      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.08 : 0.92;
      const centerX = this.cropRect.x + this.cropRect.width / 2;
      const centerY = this.cropRect.y + this.cropRect.height / 2;
      this.scaleCropRect(factor, { x: centerX, y: centerY });
    }

    async confirmCrop() {
      if (!this.cropImageElement || !this.cropRect || !this.cropImageRect || !this.cropConfirmButton) return;

      this.cropConfirmButton.disabled = true;
      this.cropConfirmButton.setAttribute('aria-busy', 'true');

      try {
        const file = await this.exportCropFile();

        if (this.isCustomerUploadMode()) {
          this.replaceCustomerUploadWithCrop(file);
          this.setWarning('');
          this.closeCropModal();
          return;
        }

        this.cropFile = file;
        this.setCropPreference(this.getMessage('customerCrop', 'Customer crop'));
        this.setCropFileInput(file);
        this.setCropPreviewFile(file);
        this.updateCropStatus();
        this.setWarning('');
        this.closeCropModal();
      } catch (error) {
        this.clearCropSelection();
        const exportError = this.getMessage('unableToExportCrop', 'Unable to export the crop. Our designers can adjust it for you.');
        this.setCropModalMessage(exportError);
        this.setWarning(exportError);
      } finally {
        this.cropConfirmButton.disabled = false;
        this.cropConfirmButton.removeAttribute('aria-busy');
      }
    }

    async exportCropFile() {
      const scaleX = this.cropImageElement.naturalWidth / this.cropImageRect.width;
      const scaleY = this.cropImageElement.naturalHeight / this.cropImageRect.height;
      const sx = clamp((this.cropRect.x - this.cropImageRect.x) * scaleX, 0, this.cropImageElement.naturalWidth);
      const sy = clamp((this.cropRect.y - this.cropImageRect.y) * scaleY, 0, this.cropImageElement.naturalHeight);
      const sw = clamp(this.cropRect.width * scaleX, 1, this.cropImageElement.naturalWidth - sx);
      const sh = clamp(this.cropRect.height * scaleY, 1, this.cropImageElement.naturalHeight - sy);
      const outputScale = Math.min(1, maxCropExportDimension / Math.max(sw, sh));
      const outputCanvas = document.createElement('canvas');
      const outputContext = outputCanvas.getContext('2d');

      outputCanvas.width = Math.max(1, Math.round(sw * outputScale));
      outputCanvas.height = Math.max(1, Math.round(sh * outputScale));
      outputContext.drawImage(this.cropImageElement, sx, sy, sw, sh, 0, 0, outputCanvas.width, outputCanvas.height);

      const blob = await canvasToBlob(outputCanvas, cropExportType, cropExportQuality);
      const filePrefix = this.isCustomerUploadMode() ? `custom-upload-${this.activeCropImageIndex + 1}-crop` : 'custom-size-crop';

      return new File([blob], `${filePrefix}-${Date.now()}.jpg`, {
        type: cropExportType,
        lastModified: Date.now(),
      });
    }

    update() {
      this.currentVariant = this.findVariantBySelectedOptions() || this.findVariantById(this.getCurrentVariantId()) || this.currentVariant;

      const targetVariantSelected = this.isCustomVariantSelected();
      const customSizeVisible = targetVariantSelected;
      const fixedVariantDimension = this.syncFixedVariantDimension(customSizeVisible);
      const fixedVariantSelected = Boolean(fixedVariantDimension);
      const customSizePricingSelected = targetVariantSelected && !fixedVariantSelected;
      const dimensionsEditable = customSizePricingSelected;
      const materialKey = this.getMaterialKey();
      const materialRule = this.getMaterialRule(materialKey);

      this.root.hidden = !customSizeVisible;
      this.root.toggleAttribute('data-custom-size-media-only', fixedVariantSelected);
      this.body.hidden = !customSizeVisible;
      this.widthInput.required = customSizePricingSelected;
      this.heightInput.required = customSizePricingSelected;
      this.setDimensionInputsState({
        editable: dimensionsEditable,
        widthRaw: fixedVariantDimension ? fixedVariantDimension.widthRaw : '',
        heightRaw: fixedVariantDimension ? fixedVariantDimension.heightRaw : '',
        unit: fixedVariantDimension ? fixedVariantDimension.unit : this.unitSelect.value || this.config.defaultUnit || 'ft',
      });
      this.updateDynamicCheckoutVisibility(customSizeVisible);

      if (this.materialOutput) {
        this.materialOutput.textContent =
          customSizePricingSelected && materialRule && materialRule.name ? materialRule.name : '';
      }

      if (!customSizeVisible) {
        this.updateSeamNotice(null);
        this.clearHiddenFields();
        this.clearCropSelection({ resetPreference: false });
        this.clearCustomerUploads();
        this.currentDimensionSignature = '';
        this.clearCropDimensionLabel();
        this.cropAspectRatio = null;
        this.updateCropUi(false);
        this.setMessage('');
        this.setWarning('');
        this.result.hidden = true;
        this.updateAddToCartButton(false);
        this.restoreVariantPriceDisplays();
        return;
      }

      const widthRaw = fixedVariantDimension ? fixedVariantDimension.widthRaw : this.widthInput.value.trim();
      const heightRaw = fixedVariantDimension ? fixedVariantDimension.heightRaw : this.heightInput.value.trim();
      const unit = fixedVariantDimension ? fixedVariantDimension.unit : this.unitSelect.value || this.config.defaultUnit || 'ft';
      const width = toNumber(widthRaw);
      const height = toNumber(heightRaw);
      const dimensions = width !== null && height !== null ? this.getNormalizedDimensions(width, height, unit) : null;

      this.clearHiddenFields();

      if (fixedVariantSelected) {
        const dimensionSignature = this.getDimensionSignature(widthRaw, heightRaw, unit);
        if (this.currentDimensionSignature && this.currentDimensionSignature !== dimensionSignature) {
          this.clearCropSelection();
        }
        this.currentDimensionSignature = dimensionSignature;
        this.cropAspectRatio = width / height;
        this.setCropDimensionLabel(widthRaw, heightRaw, unit);
        this.updateCropUi(true);
        this.updateSeamNotice(null);
        this.setMessage('');
        this.setWarning('');
        this.result.hidden = true;
        this.updateAddToCartButton(false);
        this.restoreVariantPriceDisplays();
        return;
      }

      const error = this.validate(widthRaw, heightRaw, unit, materialRule, dimensions);

      this.setMessage(error);
      this.result.hidden = Boolean(error);
      this.updateAddToCartButton(Boolean(error));

      if (error) {
        this.clearCropSelection();
        this.currentDimensionSignature = '';
        this.clearCropDimensionLabel();
        this.cropAspectRatio = null;
        this.updateCropUi(false);
        this.updateSeamNotice(null);
        this.setWarning('');
        this.restoreVariantPriceDisplays();
        return;
      }

      const dimensionSignature = this.getDimensionSignature(widthRaw, heightRaw, unit);
      if (this.currentDimensionSignature && this.currentDimensionSignature !== dimensionSignature) {
        this.clearCropSelection();
      }
      this.currentDimensionSignature = dimensionSignature;
      this.cropAspectRatio = width / height;
      this.setCropDimensionLabel(widthRaw, heightRaw, unit);
      this.updateCropUi(true);
      this.updateSeamNotice(this.shouldShowSeamNotice(materialKey, width, height, unit) ? materialKey : null);

      const price = this.calculatePrice(dimensions, materialRule);
      const cents = Math.round(price * 100);
      const formattedPrice = formatMoney(cents);

      this.priceOutput.innerHTML = formattedPrice;
      this.updatePriceDisplays(formattedPrice);
      this.updateHiddenFields(widthRaw, heightRaw, unit, materialRule, dimensions, price);
      this.setWarning('');
    }
  }

  const initCustomSizePricing = (root) => {
    if (!root || root.getAttribute(initializedAttribute) === 'true') return;

    root.setAttribute(initializedAttribute, 'true');
    root.customSizePricingController = new CustomSizePricingController(root);
  };

  const initAll = () => {
    document.querySelectorAll('custom-size-pricing').forEach(initCustomSizePricing);
  };

  if (window.customElements && !window.customElements.get('custom-size-pricing')) {
    window.customElements.define(
      'custom-size-pricing',
      class extends HTMLElement {
        connectedCallback() {
          initCustomSizePricing(this);
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
